import React, { ReactNode, useRef, useState } from 'react';
import { linkTo, Source } from '@99designs/feature-auth';
import {
    Box,
    SolidAnchorButton,
    SolidButton,
    Stack,
    SuccessAlert,
    ErrorAlert,
} from '@99designs/ui';
import is99StaffEmail from './common/is99StaffEmail';
import { Layout } from './common/Layout';

import Bugsnag from '@bugsnag/js';

import { BaseLink, NoWrap, useSnackbar } from '@99designs/purgatory';
import { useForm } from 'react-hook-form';
import { Checkbox, TextField } from './Form';
import { BackendErrorType, getErrorForField, setFieldErrors } from './common/utils';
import Captcha from './Captcha';
import AuthLink from './AuthLink';
import { partitionArray } from '@99designs/apps/spa/libs/utils/partitionArray/partitionArray';
import OAuthContinueButton from '@99designs/apps/spa/common/OAuthContinueButton';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { useAuthConfigContext } from './AuthConfigContext';
import { PageType } from './types';

export interface LoginProps {
    clientError?: string;
    onSetPage: (
        e: React.MouseEvent<HTMLAnchorElement> | null,
        page: PageType,
        link: string | null
    ) => void;
    onSubmit: (
        endpoint: 'login' | 'signup',
        data: Record<string, unknown>
    ) => Promise<{ status: number; body: any }>;
    resetSuccessReason?: string;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
}

interface FormState {
    email: string;
    password: string;
    rememberMe: string;
}

const resetSuccessMessageMap: Record<string, string> = {
    'password-reset': __('Password updated. Please log in to continue.'),
    'email-updated': __('Email updated. Please log in to continue.'),
};

const errorMessageMap: Record<string, string | ReactNode> = {
    'oauth-failed': __('Could not connect. Try again or log in with your email.'),
    'oauth-account-creation': (
        <FormattedReactMessage
            message={__(
                'Your account could not be created. Please contact <Link>Customer Support</Link> for assistance.'
            )}
            replacements={{
                Link: <BaseLink href={linkTo.helpContact()} color="bleachedAs" />,
            }}
        />
    ),
    'oauth-password': __(
        'It looks like you have an existing 99designs account. Please log in with your email.'
    ),
    geo: __(`Registrations aren't available in your country`),
    'unknown-error': __('An unknown error has occurred. Try again or log in with your email.'),
    'cant-login': __("Sorry, you can't log in at this time."),
};

function Login({
    clientError,
    onSetPage,
    onSubmit,
    resetSuccessReason,
    showRecaptcha = true,
    source,
    target,
}: LoginProps) {
    const { recaptchaSiteKey } = useAuthConfigContext();
    const recaptchaRef = useRef<any>(null);
    const [showClientError, setShowClientError] = useState(true);
    const [recaptchaError, setRecaptchaError] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [force2FA, setForce2FA] = useState<boolean>(false);
    const {
        register,
        handleSubmit,
        watch,
        errors,
        formState: { isSubmitting },
        setError,
        getValues,
    } = useForm<FormState>({
        defaultValues: {
            email: '',
        },
    });
    const snackbar = useSnackbar();

    const showOktaButton = is99StaffEmail(watch('email'));

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function handleRecaptchaError() {
        // Force 2FA if recaptcha fails to load
        setForce2FA(true);
    }

    function handleDismissError() {
        setShowClientError(false);
    }

    function submitForm({ email, password, rememberMe }: FormState) {
        const needsRecaptcha = !force2FA && showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        return onSubmit('login', {
            email,
            password,
            rememberMe,
            recaptchaToken,
            force_2fa: force2FA,
        })
            .then((d) => {
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                    setRecaptchaToken(null);
                }

                if (d.body.errors) {
                    const errors = partitionArray(d.body.errors, (error: BackendErrorType) => {
                        return Object.keys(getValues()).includes(error.param);
                    });
                    errors.nonMatches.forEach((item: BackendErrorType) => {
                        switch (item.param) {
                            case 'captcha':
                                setRecaptchaError(item.message);
                                break;
                            default:
                                snackbar.error(__('Something went wrong, please try again later.'));
                                Bugsnag.notify(`${item.param} : ${item.message}`, (event) => {
                                    event.context = 'Logging in';
                                });
                                break;
                        }
                    });

                    setFieldErrors(errors.matches, setError);
                    return;
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Logging in';
                });
            });
    }

    return (
        <Layout
            title={__('Log in')}
            description={__('Log in to your 99designs account')}
            heading={__('Log in')}
        >
            <form onSubmit={handleSubmit(submitForm)}>
                <Stack space="space8">
                    {resetSuccessReason && resetSuccessMessageMap[resetSuccessReason] && (
                        <SuccessAlert data-testid="reset-success-message">
                            {resetSuccessMessageMap[resetSuccessReason]}
                        </SuccessAlert>
                    )}
                    {showClientError && clientError && errorMessageMap[clientError] && (
                        <ErrorAlert
                            data-testid="error-message"
                            onDismiss={() => {
                                handleDismissError();
                            }}
                        >
                            {errorMessageMap[clientError]}
                        </ErrorAlert>
                    )}
                    <TextField
                        error={getErrorForField('email', errors)}
                        id="email"
                        name="email"
                        type="text"
                        label={__('Email')}
                        ref={register({ required: __('Enter your email address') })}
                        autoFocus
                        autoComplete="username"
                    />

                    <TextField
                        error={getErrorForField('password', errors)}
                        id="password"
                        name="password"
                        type="password"
                        label={__('Password')}
                        ref={register({ required: __('Enter your password') })}
                        autoComplete="current-password"
                    />

                    <Box display="flex" justifyContent="space-between">
                        <Checkbox ref={register} id="remember_me" name="rememberMe">
                            <label htmlFor="remember_me">{__('Remember me')}</label>
                        </Checkbox>
                        <NoWrap>
                            <BaseLink href={linkTo.forgotPassword()}>
                                {__('Forgot password?')}
                            </BaseLink>
                        </NoWrap>
                    </Box>
                    {showRecaptcha && !!recaptchaSiteKey && (
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            onErrored={handleRecaptchaError}
                            error={recaptchaError}
                        />
                    )}

                    <SolidButton
                        data-testid="login-button"
                        name="login-button"
                        processing={isSubmitting}
                        size="large"
                        variant="primary"
                        width="fill"
                    >
                        {__('Log in')}
                    </SolidButton>

                    <OAuthContinueButton
                        target={target}
                        source={source}
                        action="login"
                        provider="Vistaprint"
                    />

                    {showOktaButton && (
                        <SolidAnchorButton
                            href={linkTo.authSAMLRedirect(target)}
                            data-testid="okta-button"
                            size="large"
                            variant="secondary"
                            width="fill"
                        >
                            Log in via Okta
                        </SolidAnchorButton>
                    )}
                </Stack>
            </form>
            <p>
                {__("Don't have an account?")}{' '}
                <AuthLink
                    data-testid="signup-link"
                    onSetPage={onSetPage}
                    page="signup"
                    to={linkTo.signup(undefined, target)}
                >
                    {__('Sign up')}
                </AuthLink>
            </p>
        </Layout>
    );
}
export default Login;
